import React, { useEffect, useMemo, useState } from "react";
import { Box, capitalize, Checkbox, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getNeonColor, scrollable } from "../../theme";
import { useReferentialContext } from "../../Context";
import { Maps, ScrimGames } from "../../reckon-api";
import { NeonToggleButton } from "../../NeonComponents/NeonToggle";
import { NeonButton } from "../../NeonComponents/Buttons";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import TeamIcon from "../../components/Icons/TeamIcon";
import dayjs, { Dayjs } from "dayjs";

interface MapStats {
    map: string;
    games: number;
    wins: number;
    losses: number;
    winRate: number;
}

function buildMapStats(games: ScrimGames[], userTeam: string): MapStats[] {
    const byMap = new Map<string, { wins: number; total: number }>();

    for (const g of games) {
        const isMyTeam = g.team1 === userTeam || g.team2 === userTeam;
        if (!isMyTeam) continue;

        const entry = byMap.get(g.map) ?? { wins: 0, total: 0 };
        entry.total += 1;
        if (g.winner === userTeam) entry.wins += 1;
        byMap.set(g.map, entry);
    }

    const result: MapStats[] = [];
    for (const [map, data] of byMap) {
        result.push({
            map,
            games: data.total,
            wins: data.wins,
            losses: data.total - data.wins,
            winRate: data.total > 0 ? data.wins / data.total : 0,
        });
    }

    return result.sort((a, b) => b.games - a.games);
}

export default function ScrimsMapStats() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const userTeam = ctx.user.team;

    const [selectedMaps, setSelectedMaps] = useState<Maps[]>([]);
    const [games, setGames] = useState<ScrimGames[]>([]);
    const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState<Dayjs>(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState<Dayjs>(dayjs());

    useEffect(() => {
        setLoading(true);
        const additional_filters: Record<string, string> = {
            date__gte: from.toISOString(),
            date__lt: until.toISOString(),
        };
        if (selectedMaps.length > 0) {
            additional_filters.map__in = selectedMaps.map((m) => m.id).join(",");
        }
        ctx.reckonApi.scrimGames
            .list({
                ordering: ["-date"],
                additional_filters,
            })
            .then((result) => {
                setGames(result);
                setSelectedGameIds(new Set(result.map((g) => g.id)));
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [from, until, selectedMaps]);

    const myGames = useMemo(
        () => games.filter((g) => g.team1 === userTeam || g.team2 === userTeam),
        [games, userTeam],
    );

    const toggleGame = (id: string) => {
        setSelectedGameIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const activeGames = useMemo(
        () => myGames.filter((g) => selectedGameIds.has(g.id)),
        [myGames, selectedGameIds],
    );

    const mapStats = useMemo(() => buildMapStats(activeGames, userTeam), [activeGames, userTeam]);

    const toggleMap = (map: Maps) => {
        if (selectedMaps.find((m) => m.id === map.id)) {
            setSelectedMaps(selectedMaps.filter((m) => m.id !== map.id));
        } else {
            setSelectedMaps([...selectedMaps, map]);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                p: { xs: 1, sm: 2, md: 3 },
                ...scrollable,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <DatePicker value={from} onChange={(v: Dayjs | null) => v && setFrom(v)} label="From" />
                <DatePicker value={until} onChange={(v: Dayjs | null) => v && setUntil(v)} label="Until" />
            </Stack>

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                <NeonButton variant="outlined" onClick={() => setSelectedMaps([...ctx.mapsReferential])}>
                    Select All
                </NeonButton>
                <NeonButton variant="outlined" onClick={() => setSelectedMaps([])}>
                    Unselect All
                </NeonButton>
                {ctx.mapsReferential.map((map) => (
                    <NeonToggleButton
                        key={map.id}
                        value={map.id}
                        selected={selectedMaps.some((m) => m.id === map.id)}
                        onClick={() => toggleMap(map)}
                        sx={{ minHeight: 40, px: 2, py: 0.5, fontSize: "0.85rem" }}
                    >
                        {capitalize(map.id)}
                    </NeonToggleButton>
                ))}
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : (
                <Stack direction="row" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
                    {/* Left: Game list */}
                    <NeonCard sx={{ width: 360, minWidth: 360, flex: "0 0 auto", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <NeonCardHeader
                            title="Games"
                            action={
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                                        {activeGames.length}/{myGames.length}
                                    </Typography>
                                    <NeonButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setSelectedGameIds(new Set(myGames.map((g) => g.id)))}
                                        sx={{ fontSize: "0.7rem", px: 1, py: 0.25, minWidth: 0 }}
                                    >
                                        All
                                    </NeonButton>
                                    <NeonButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setSelectedGameIds(new Set())}
                                        sx={{ fontSize: "0.7rem", px: 1, py: 0.25, minWidth: 0 }}
                                    >
                                        None
                                    </NeonButton>
                                </Stack>
                            }
                            sx={{ pb: 0 }}
                        />
                        <NeonCardContent sx={{ flexDirection: "column", gap: 0, pt: 1, ...scrollable }}>
                            {myGames.map((g) => {
                                const won = g.winner === userTeam;
                                const isTeam1 = g.team1 === userTeam;
                                const opponent = isTeam1 ? g.team2 : g.team1;
                                const myScore = isTeam1 ? g.score_team1 : g.score_team2;
                                const enemyScore = isTeam1 ? g.score_team2 : g.score_team1;

                                const checked = selectedGameIds.has(g.id);

                                return (
                                    <Box key={g.id}>
                                        <Stack
                                            direction="row"
                                            onClick={() => toggleGame(g.id)}
                                            sx={{
                                                alignItems: "center",
                                                py: 0.75,
                                                px: 1,
                                                borderLeft: `3px solid ${won ? "#4caf50" : "#f44336"}`,
                                                borderRadius: 0.5,
                                                cursor: "pointer",
                                                opacity: checked ? 1 : 0.4,
                                                transition: "opacity 0.2s",
                                                "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                                            }}
                                            spacing={1}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                tabIndex={-1}
                                                sx={{ color: neonColor, "&.Mui-checked": { color: neonColor }, p: 0 }}
                                            />
                                            <Chip
                                                label={capitalize(g.map)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: "0.7rem", minWidth: 60 }}
                                            />
                                            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flex: 1, justifyContent: "center" }}>
                                                <TeamIcon team_name={userTeam} sx={{ width: 18, height: 18 }} />
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: won ? "lightgreen" : "error.main" }}>
                                                    {myScore}
                                                </Typography>
                                                <Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>-</Typography>
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: !won ? "lightgreen" : "error.main" }}>
                                                    {enemyScore}
                                                </Typography>
                                                <TeamIcon team_name={opponent} sx={{ width: 18, height: 18 }} />
                                            </Stack>
                                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", whiteSpace: "nowrap" }}>
                                                {dayjs(g.date).format("MMM D")}
                                            </Typography>
                                        </Stack>
                                        <Divider sx={{ opacity: 0.15 }} />
                                    </Box>
                                );
                            })}
                            {myGames.length === 0 && (
                                <Typography sx={{ color: "text.secondary", py: 2, textAlign: "center" }}>
                                    No games found.
                                </Typography>
                            )}
                        </NeonCardContent>
                    </NeonCard>

                    {/* Right: Map stats cards */}
                    <Stack spacing={2} sx={{ flex: 1, flexWrap: "wrap", flexDirection: "row", gap: 2, alignContent: "flex-start" }}>
                        {mapStats.map((stat) => (
                            <NeonCard
                                key={stat.map}
                                sx={{ minWidth: 200, flex: "0 0 auto", height: "fit-content" }}
                                glow_color={stat.winRate >= 0.5 ? "#4caf50" : "#f44336"}
                            >
                                <NeonCardHeader title={capitalize(stat.map)} sx={{ pb: 0 }} />
                                <NeonCardContent sx={{ flexDirection: "column", gap: 1, pt: 1 }}>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {stat.games} game{stat.games !== 1 ? "s" : ""}
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 700,
                                                color: stat.winRate >= 0.5 ? "lightgreen" : "error.main",
                                            }}
                                        >
                                            {(stat.winRate * 100).toFixed(0)}%
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            {stat.wins}W - {stat.losses}L
                                        </Typography>
                                    </Stack>
                                </NeonCardContent>
                            </NeonCard>
                        ))}
                        {mapStats.length === 0 && (
                            <Typography sx={{ color: "text.secondary", py: 4 }}>
                                No scrim games found.
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}
        </Box>
    );
}
